import React, { useEffect, useState } from "react";
import Loader from "../../../../components/Loader";
import {
  Button,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import DataGridComponent from "../../../../components/DataGridComponent";
import { showSnackbar } from "../../../../slice/snackbarSlice";
import { useDispatch } from "react-redux";
import {
  getTaskPickingPermission,
  giveTaskPickupPermission,
} from "../../../../thunks/instruction";
import {
  createWorkflowConfiguration,
  getWorkflowConfiguration,
} from "../../../../thunks/workflow";

const TaskPermissions = () => {
  const dispatch = useDispatch();
  const [isLoader, setLoader] = useState(false);
  const [taskPickup, setTaskPickup] = useState({
    numberOfMainTasks: "",
    numberOfSubTasks: "",
    isMainTaskOutcome: false,
  });

  // Fixed initial state - defaults to "No" (false)
  const [workflowConfiguration, setWorkflowConfiguration] = useState({
    key: "showOutcomesOfMainTasks",
    value: false, // Default to "No"
    valueType: "boolean",
  });

  const getTaskPickupData = async () => {
    try {
      setLoader(true);
      const response = await dispatch(getTaskPickingPermission()).unwrap();
      if (response) {
        const data = Array.isArray(response) ? response[0] : response;

        setTaskPickup({
          numberOfMainTasks:
            data.numberOfMainTasks === 100000
              ? "No Limit"
              : data.numberOfMainTasks.toString(),
          numberOfSubTasks:
            data.numberOfSubTasks === 100000
              ? "No Limit"
              : data.numberOfSubTasks.toString(),
          isMainTaskOutcome: !!data.isMainTaskOutcome,
        });
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          show: true,
          message: "Something went wrong while fetching task permissions",
          severity: "error",
        })
      );
    } finally {
      setLoader(false);
    }
  };

  // Fixed getWorkflowConfiguration function
  const getWorkflowConfigurationData = async () => {
    try {
      const response = await dispatch(getWorkflowConfiguration()).unwrap();

      // Check if response has configuration data (array of configurations)
      if (
        response &&
        response.configuration &&
        response.configuration.length > 0
      ) {
        // Find the specific configuration for "showOutcomesOfMainTasks"
        const targetConfig = response.configuration.find(
          (config) => config.key === "showOutcomesOfMainTasks"
        );

        if (targetConfig) {
          setWorkflowConfiguration({
            key: targetConfig.key,
            value: !!targetConfig.value,
            valueType: targetConfig.valueType || "boolean",
          });
        } else {
          // Configuration exists but not for our specific key, use defaults
          // console.log(
          //   "showOutcomesOfMainTasks configuration not found, using defaults (No)"
          // );
          setWorkflowConfiguration({
            key: "showOutcomesOfMainTasks",
            value: false,
            valueType: "boolean",
          });
        }
      } else if (response && response.key) {
        // Handle case where response is directly the configuration object
        if (response.key === "showOutcomesOfMainTasks") {
          setWorkflowConfiguration({
            key: response.key,
            value: !!response.value,
            valueType: response.valueType || "boolean",
          });
        } else {
          // Different configuration, use defaults
          setWorkflowConfiguration({
            key: "showOutcomesOfMainTasks",
            value: false,
            valueType: "boolean",
          });
        }
      } else {
        // No configuration found, keep default values (No option selected)
        // console.log("No workflow configuration found, using defaults (No)");
        setWorkflowConfiguration({
          key: "showOutcomesOfMainTasks",
          value: false,
          valueType: "boolean",
        });
      }
    } catch (error) {
      // console.log(
      //   "Workflow configuration not found or error occurred, using defaults (No)"
      // );
      // On error, ensure we default to "No"
      setWorkflowConfiguration({
        key: "showOutcomesOfMainTasks",
        value: false,
        valueType: "boolean",
      });
    }
  };

  // Fixed saveWorkflowConfiguration function
  const saveWorkflowConfiguration = async (value) => {
    try {
      const payload = {
        configuration: [
          {
            key: "showOutcomesOfMainTasks",
            value: value,
            valueType: "boolean",
            description:
              "Determines whether outcomes of main tasks are visible",
          },
        ],
      };

      const response = await dispatch(
        createWorkflowConfiguration(payload)
      ).unwrap();

      // Update state based on the response structure you provided
      if (
        response &&
        response.configuration &&
        response.configuration.length > 0
      ) {
        // Find the specific configuration that was just created/updated
        const targetConfig = response.configuration.find(
          (config) => config.key === "showOutcomesOfMainTasks"
        );

        if (targetConfig) {
          setWorkflowConfiguration({
            key: targetConfig.key,
            value: !!targetConfig.value, // Ensure boolean
            valueType: targetConfig.valueType,
          });
        } else {
          // Fallback to the first configuration if specific key not found
          setWorkflowConfiguration({
            key: response.configuration[0].key,
            value: !!response.configuration[0].value,
            valueType: response.configuration[0].valueType,
          });
        }
      }

      dispatch(
        showSnackbar({
          show: true,
          message: "Workflow configuration updated successfully",
          severity: "success",
        })
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          show: true,
          message: "Failed to update workflow configuration",
          severity: "error",
        })
      );
    }
  };

  const handleChange = (key) => (event) => {
    const value = event.target.value;

    if (value === "") {
      setTaskPickup((prev) => ({
        ...prev,
        [key]: "",
      }));
      return;
    }

    if (value.toLowerCase() === "no limit") {
      setTaskPickup((prev) => ({
        ...prev,
        [key]: "No Limit",
      }));
      return;
    }

    const noLimitPartial = "no limit".substring(0, value.toLowerCase().length);
    if (value.toLowerCase() === noLimitPartial) {
      setTaskPickup((prev) => ({
        ...prev,
        [key]: value,
      }));
      return;
    }

    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 0) {
      setTaskPickup((prev) => ({
        ...prev,
        [key]: value,
      }));
    }
  };

  const handleWorkflowConfigurationChange = (event) => {
    const newValue = event.target.value === "Yes";

    setWorkflowConfiguration((prev) => ({
      ...prev,
      value: newValue,
    }));
  };

  const validateForm = () => {
    if (!taskPickup.numberOfMainTasks) {
      dispatch(
        showSnackbar({
          show: true,
          message: "Please enter number of main tasks",
          severity: "error",
        })
      );
      return false;
    }

    if (!taskPickup.numberOfSubTasks) {
      dispatch(
        showSnackbar({
          show: true,
          message: "Please enter number of sub tasks",
          severity: "error",
        })
      );
      return false;
    }

    if (
      taskPickup.numberOfMainTasks.toLowerCase() !== "no limit" &&
      (isNaN(parseInt(taskPickup.numberOfMainTasks)) ||
        parseInt(taskPickup.numberOfMainTasks) < 0)
    ) {
      dispatch(
        showSnackbar({
          show: true,
          message: "Main task number must be a valid non-negative number",
          severity: "error",
        })
      );
      return false;
    }

    if (
      taskPickup.numberOfSubTasks.toLowerCase() !== "no limit" &&
      (isNaN(parseInt(taskPickup.numberOfSubTasks)) ||
        parseInt(taskPickup.numberOfSubTasks) < 0)
    ) {
      dispatch(
        showSnackbar({
          show: true,
          message: "Sub task number must be a valid non-negative number",
          severity: "error",
        })
      );
      return false;
    }

    return true;
  };

  const handleGivePermission = async () => {
    if (!validateForm()) return;

    try {
      setLoader(true);

      // Save workflow configuration first
      await saveWorkflowConfiguration(workflowConfiguration.value);

      // Then save task permissions
      const payload = {
        numberOfMainTasks:
          taskPickup.numberOfMainTasks.toLowerCase() === "no limit"
            ? 100000
            : parseInt(taskPickup.numberOfMainTasks) || 0,
        numberOfSubTasks:
          taskPickup.numberOfSubTasks.toLowerCase() === "no limit"
            ? 100000
            : parseInt(taskPickup.numberOfSubTasks) || 0,
        autoPickSubTasks: "Yes",
        overrideSubTaskPick: "No",
        isMainTaskOutcome: workflowConfiguration.value,
      };

      await dispatch(giveTaskPickupPermission(payload)).unwrap();
      await getTaskPickupData();

      dispatch(
        showSnackbar({
          show: true,
          message: "Task permissions saved successfully",
          severity: "success",
        })
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          show: true,
          message: error?.message || "An error occurred while saving",
          severity: "error",
        })
      );
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    getTaskPickupData();
    getWorkflowConfigurationData();
  }, []);

  return (
    <Grid container spacing={3} paddingLeft={4}>
      {isLoader && <Loader />}
      <Grid item xs={7} mt={2}>
        <Grid container xs={12}>
          <Grid item xs={12} mb={2}>
            <Typography className="serif_display_regular_22 green">
              Task Pickup Permission
            </Typography>
          </Grid>

          <Grid
            container
            spacing={2}
            style={{ display: "flex", alignItems: "center" }}
          >
            <Grid item xs={10.1}>
              <TextField
                name="numberOfMainTasks"
                label='How many main tasks can be picked up at once? (Min 1 when "no limit" set limit to 1 Lakh)'
                className="pickupInputTextField border_color pick_normal_normal_20_Manropee dark_grey"
                variant="standard"
                size="small"
                value={taskPickup.numberOfMainTasks}
                onChange={handleChange("numberOfMainTasks")}
                required
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Enter a number or type 'No Limit'"
              />
            </Grid>

            <Grid item xs={10}>
              <TextField
                name="numberOfSubTasks"
                label='How many sub tasks can be picked up at once? (Min 1 when "no limit" set limit to 1 Lakh)'
                className="pickupInputTextField border_color pick_normal_normal_20_Manropee dark_grey"
                variant="standard"
                size="small"
                value={taskPickup.numberOfSubTasks}
                onChange={handleChange("numberOfSubTasks")}
                required
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Enter a number or type 'No Limit'"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography className="pick_normal_normal_16_Manrope dark_grey">
                If a main task is picked up, would you like all associated
                sub-tasks to be picked up automatically? (Yes)
              </Typography>
              <RadioGroup fullWidth row value="Yes">
                <FormControlLabel
                  className="radioLabel"
                  value="Yes"
                  control={
                    <Radio
                      size="small"
                      disabled
                      sx={{ "&, &.Mui-checked": { color: "#00CE3F" } }}
                    />
                  }
                  label="Yes"
                />
              </RadioGroup>

              <Typography
                mt={2}
                className="pick_normal_normal_16_Manrope dark_grey"
              >
                If a main task is picked up, would you like to override anyone
                who has already picked up an associated sub-task? (No)
              </Typography>
              <RadioGroup fullWidth row value="No">
                <FormControlLabel
                  className="radioLabel"
                  value="No"
                  control={
                    <Radio
                      size="small"
                      disabled
                      sx={{ "&, &.Mui-checked": { color: "#00CE3F" } }}
                    />
                  }
                  label="No"
                />
              </RadioGroup>
            </Grid>

            <Grid item xs={12}>
              <Typography className="pick_normal_normal_16_Manrope dark_grey">
                Do you want to display the outcomes of main tasks that have
                subtasks?
              </Typography>

              <RadioGroup
                fullWidth
                row
                value={workflowConfiguration.value ? "Yes" : "No"}
                onChange={handleWorkflowConfigurationChange}
              >
                <FormControlLabel
                  className="radioLabel"
                  value="Yes"
                  control={
                    <Radio
                      size="small"
                      sx={{ "&, &.Mui-checked": { color: "#00CE3F" } }}
                    />
                  }
                  label="Yes"
                />
                <FormControlLabel
                  className="radioLabel"
                  value="No"
                  control={
                    <Radio
                      size="small"
                      sx={{ "&, &.Mui-checked": { color: "#00CE3F" } }}
                    />
                  }
                  label="No"
                />
              </RadioGroup>
            </Grid>

            <Grid
              item
              mt={3}
              mb={3}
              xs={10.5}
              sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
            >
              <Button className="save_button" onClick={handleGivePermission}>
                Save
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default TaskPermissions;
